// Direct Fetch Utility for Supabase REST API
// Use this when Supabase client hangs but direct REST API works
// Credentials are imported from centralized database config

import { SUPABASE_URL, SUPABASE_KEY, getSupabaseHeaders } from '../config/database';

// Standard headers for all requests
const getHeaders = getSupabaseHeaders;

// Build query string from filters
const buildQueryString = (options = {}) => {
  const params = new URLSearchParams();
  
  // Select fields
  if (options.select) {
    params.append('select', options.select);
  } else {
    params.append('select', '*');
  }
  
  // Filters
  if (options.eq) {
    Object.entries(options.eq).forEach(([key, value]) => {
      params.append(key, `eq.${value}`);
    });
  }
  
  if (options.in) {
    Object.entries(options.in).forEach(([key, values]) => {
      params.append(key, `in.(${values.join(',')})`);
    });
  }
  
  if (options.neq) {
    Object.entries(options.neq).forEach(([key, value]) => {
      params.append(key, `neq.${value}`);
    });
  }
  
  if (options.is) {
    Object.entries(options.is).forEach(([key, value]) => {
      params.append(key, `is.${value}`);
    });
  }

  // not.is filter: matches rows where column is NOT the given value (including NULLs)
  if (options.not_is) {
    Object.entries(options.not_is).forEach(([key, value]) => {
      params.append(key, `not.is.${value}`);
    });
  }
  
  // Ordering. `order2` is a tiebreak applied after `order` — PostgREST takes a
  // comma-separated list in one `order` parameter. Paging without a total order
  // is unsound: rows that tie on the primary key of the sort can be returned on
  // two different pages while other rows are skipped entirely, which matters
  // for bulk imports where every row shares a created_at.
  if (options.order) {
    const terms = [options.order, options.order2]
      .filter(Boolean)
      .map(({ column, ascending = true }) => `${column}.${ascending ? 'asc' : 'desc'}`);
    params.append('order', terms.join(','));
  }
  
  // Limit
  if (options.limit) {
    params.append('limit', options.limit);
  }

  // Offset
  if (options.offset) {
    params.append('offset', options.offset);
  }
  
  return params.toString();
};

// Main query function
export const directQuery = async (table, options = {}) => {
  const { fetchAll = false, pageSize = 200, ...queryOptions } = options;
  const baseQueryString = buildQueryString(queryOptions);
  const baseUrl = `${SUPABASE_URL}/rest/v1/${table}`;

  // Create unique timer name to avoid conflicts
  const timerName = `DIRECT-FETCH: ${table}-${Date.now()}`;

  const headers = await getHeaders();
  console.log(`🔍 [DIRECT-FETCH] ${table} query:`, `${baseUrl}?${baseQueryString}`);
  console.log(`🔐 [DIRECT-FETCH] Headers being sent:`, {
    apikey: headers.apikey ? headers.apikey.substring(0, 20) + '...' : 'MISSING',
    Authorization: headers.Authorization ? headers.Authorization.substring(0, 30) + '...' : 'MISSING',
    'Content-Type': headers['Content-Type']
  });
  console.time(timerName);

  try {
    const fetchOnePage = async (pageOptions = {}) => {
      const queryString = buildQueryString({ ...queryOptions, ...pageOptions });
      const url = `${baseUrl}?${queryString}`;
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        console.error(`❌ [DIRECT-FETCH] ${table} HTTP ${response.status} body:`, errorBody);
        throw new Error(`HTTP ${response.status}: ${errorBody || response.statusText}`);
      }

      return response.json();
    };

    let data = [];
    if (fetchAll) {
      let offset = 0;
      while (true) {
        // Keep paging until we receive less than a full page.
        // This avoids silent truncation when API row caps are lower than total rows.
        // eslint-disable-next-line no-await-in-loop
        const pageData = await fetchOnePage({ limit: pageSize, offset });
        data = data.concat(pageData || []);
        if (!pageData || pageData.length < pageSize) {
          break;
        }
        offset += pageSize;
      }
    } else {
      data = await fetchOnePage();
    }

    console.timeEnd(timerName);
    console.log(`✅ [DIRECT-FETCH] ${table} completed. Records:`, data?.length || 0);

    return { data, error: null };

  } catch (fetchError) {
    console.timeEnd(timerName);
    console.error(`❌ [DIRECT-FETCH] ${table} failed:`, fetchError);
    return { data: null, error: fetchError };
  }
};

// Insert function
export const directInsert = async (table, records) => {
  const url = `${SUPABASE_URL}/rest/v1/${table}`;
  
  console.log(`🔍 [DIRECT-INSERT] ${table}:`, records);
  console.time(`DIRECT-INSERT: ${table}`);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(await getHeaders()),
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(Array.isArray(records) ? records : [records])
    });
    
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      console.error(`❌ [DIRECT-INSERT] ${table} HTTP ${response.status} body:`, errorBody);
      throw new Error(`HTTP ${response.status}: ${errorBody || response.statusText}`);
    }

    const data = await response.json();
    console.timeEnd(`DIRECT-INSERT: ${table}`);
    console.log(`✅ [DIRECT-INSERT] ${table} completed. Records:`, data?.length || 0);
    
    return { data, error: null };
    
  } catch (fetchError) {
    console.timeEnd(`DIRECT-INSERT: ${table}`);
    console.error(`❌ [DIRECT-INSERT] ${table} failed:`, fetchError);
    return { data: null, error: fetchError };
  }
};

// Update function
export const directUpdate = async (table, updates, filters) => {
  const queryString = buildQueryString({ eq: filters });
  const url = `${SUPABASE_URL}/rest/v1/${table}?${queryString}`;
  
  console.log(`🔍 [DIRECT-UPDATE] ${table}:`, updates, 'where:', filters);
  console.time(`DIRECT-UPDATE: ${table}`);
  
  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        ...(await getHeaders()),
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      console.error(`❌ [DIRECT-UPDATE] ${table} HTTP ${response.status} body:`, errorBody);
      throw new Error(`HTTP ${response.status}: ${errorBody || response.statusText}`);
    }

    const data = await response.json();
    console.timeEnd(`DIRECT-UPDATE: ${table}`);
    console.log(`✅ [DIRECT-UPDATE] ${table} completed. Records:`, data?.length || 0);
    
    return { data, error: null };
    
  } catch (fetchError) {
    console.timeEnd(`DIRECT-UPDATE: ${table}`);
    console.error(`❌ [DIRECT-UPDATE] ${table} failed:`, fetchError);
    return { data: null, error: fetchError };
  }
};

// Helper functions for common query patterns
export const queryCountSessions = (storeId, options = {}) => {
  return directQuery('count_sessions', {
    eq: { store_id: storeId },
    order: { column: 'created_at', ascending: false },
    ...options
  });
};

export const queryCountSessionsActive = (storeId) => {
  return directQuery('count_sessions', {
    eq: { store_id: storeId },
    in: { status: ['draft', 'in_progress', 'completed'] },
    not_is: { is_archived: true, is_trashed: true },
    is: { voided_at: null },
    order: { column: 'created_at', ascending: false }
  });
};

export const queryCountSessionsCompleted = (storeId, limit = 10) => {
  return directQuery('count_sessions', {
    eq: { store_id: storeId, status: 'completed' },
    not_is: { is_archived: true, is_trashed: true },
    is: { voided_at: null },
    order: { column: 'completed_at', ascending: false },
    limit
  });
};

export const queryCountSessionsArchived = (storeId) => {
  return directQuery('count_sessions', {
    eq: { store_id: storeId },
    is: { is_archived: true },
    not_is: { is_trashed: true },
    order: { column: 'archived_at', ascending: false }
  });
};

export const queryCountSessionsTrashed = (storeId) => {
  return directQuery('count_sessions', {
    eq: { store_id: storeId },
    is: { is_trashed: true },
    order: { column: 'trashed_at', ascending: false }
  });
};

export const queryInventoryItems = (storeId) => {
  return directQuery('inventory_items', {
    eq: { store_id: storeId },
    // Exclude soft-deleted/archived items (is_active=false) so deleted items
    // don't resurface in count/order item pickers. Active rows (true or NULL) stay.
    not_is: { is_active: false },
    order: { column: 'description', ascending: true }
  });
};

export const queryCountTemplates = (storeId) => {
  return directQuery('count_templates', {
    eq: { 
      store_id: storeId, 
      is_active: true 
    },
    is: { 
      is_archived: false,
      is_deleted: false 
    },
    order: { column: 'name', ascending: true }
  });
};

export const queryCountSchedules = (storeId) => {
  return directQuery('count_schedules', {
    eq: { store_id: storeId },
    order: { column: 'created_at', ascending: false }
  });
};

export default {
  directQuery,
  directInsert,
  directUpdate,
  queryCountSessions,
  queryCountSessionsActive,
  queryCountSessionsCompleted,
  queryCountSessionsArchived,
  queryCountSessionsTrashed,
  queryInventoryItems,
  queryCountTemplates,
  queryCountSchedules
};
